// frontend/src/pages/manufacturing/CustomerOrderDetail.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Grid, Card, CardContent, Typography, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, LinearProgress, IconButton, Tooltip,
  Divider, List, ListItem, ListItemText, Alert, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField,
  CircularProgress, Accordion, AccordionSummary, AccordionDetails,
  Select, MenuItem, FormControl, InputAdornment
} from '@mui/material';
import {
  ArrowBack, ShoppingCart, LocalShipping, Factory,
  CheckCircle, Warning, Edit, Delete, Inventory,
  Phone, Email, LocationOn, CalendarToday, Person,
  Build, Schedule, AttachMoney, Save, Close, Notes,
  DeleteForever, ExpandMore, Scale, Science, Restore
} from '@mui/icons-material';
import { manufacturingAPI, v2 } from '../../services/api';
import { toast } from 'react-toastify';
import { Badge } from '@mui/material'
import usePageTitle from '../../hooks/usePageTitle';
import { useAuth } from '../../contexts/AuthContext';
import OrderV2ProductionSection from './v2/components/OrderV2ProductionSection';
import OrderTimelineV2 from './v2/components/OrderTimelineV2';

// Decide whether the v1 "Create Work Orders" button should be shown, given the
// order status and the v2 feature-flag payload. Fail open: only hide v1 when the
// flag is explicitly false. A null/undefined flags object (not-yet-loaded or a
// failed fetch) leaves v1 visible so a flag-endpoint outage never blocks work
// order creation.
export function shouldShowV1CreateWorkOrders(status, flags) {
  if (status !== 'new' && status !== 'confirmed') return false;
  if (flags && flags.v1_wo_creation_enabled === false) return false;
  return true;
}

// A completed or cancelled order has its production closed: the V2 send/undo
// buttons (and per-line send/remake actions) must be hidden so there are no
// dead controls. Any other status keeps them available.
export function isOrderProductionClosed(status) {
  return status === 'complete' || status === 'cancelled';
}

// Whether the "Create Work Orders (V2)" and "Undo V2" header buttons should be
// shown for the given order status. Hidden once production is closed.
export function shouldShowV2ProductionActions(status) {
  return !isOrderProductionClosed(status);
}

export default function CustomerOrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user, hasPageAccess } = useAuth();
  const isAdmin = user?.is_staff || user?.role === 'admin';
  const canChangeStatus = isAdmin || hasPageAccess('change-order-status');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [workOrders, setWorkOrders] = useState([]);
  const [shipDialog, setShipDialog] = useState(false);
  const [selectedLine, setSelectedLine] = useState(null);
  const [shipQuantity, setShipQuantity] = useState('');
  const [shipAllDialog, setShipAllDialog] = useState(false);
  const [shippableLinesSummary, setShippableLinesSummary] = useState([]);
  const [dxConsignments, setDxConsignments] = useState([]);

  // Edit delivery info state
  const [editDeliveryDialog, setEditDeliveryDialog] = useState(false);
  const [deliveryForm, setDeliveryForm] = useState({
    delivery_name: '',
    delivery_contact_name: '',
    delivery_phone: '',
    delivery_line_1: '',
    delivery_line_2: '',
    delivery_line_3: '',
    delivery_city: '',
    delivery_county: '',
    delivery_postcode: '',
    dx_note_1: '',
    dx_note_2: '',
    dx_note_3: '',
  });
  const [savingDelivery, setSavingDelivery] = useState(false);

  // Delete order state
  const [deletePreviewDialog, setDeletePreviewDialog] = useState(false);
  const [deletePreview, setDeletePreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirmDeleteDialog, setConfirmDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Weight management state
  const [lineWeights, setLineWeights] = useState({});
  const [savingWeight, setSavingWeight] = useState({});

  usePageTitle(order ? `Order: ${order.order_number}` : null);

  useEffect(() => {
    loadOrderDetails();
    loadWorkOrders();
    loadDXConsignments();
  }, [orderId]);

  // Initialize weights when order loads
  useEffect(() => {
    if (order?.lines) {
      const initialWeights = {};
      order.lines.forEach(line => {
        initialWeights[line.id] = parseFloat(line.product_weight) || 0;
      });
      setLineWeights(initialWeights);
    }
  }, [order]);

  const loadDXConsignments = async () => {
    try {
      const response = await manufacturingAPI.dxShipping.getConsignments(orderId);
      setDxConsignments(response.data);
    } catch (error) {
      console.error('Failed to load DX consignments:', error);
    }
  };

  // Weight handlers
  const handleWeightChange = (lineId, value) => {
    const weight = parseFloat(value) || 0;
    setLineWeights(prev => ({ ...prev, [lineId]: weight }));
  };

  const handleSaveWeight = async (line) => {
    const weight = lineWeights[line.id];
    if (weight <= 0) {
      toast.error('Please enter a valid weight greater than 0');
      return;
    }
    setSavingWeight(prev => ({ ...prev, [line.id]: true }));
    try {
      const productId = line.product;
      await manufacturingAPI.items.updateWeight(productId, weight);
      toast.success(`Weight saved for ${line.product_display}: ${weight} kg`);
      setOrder(prev => ({
        ...prev,
        lines: prev.lines.map(l =>
          l.id === line.id ? { ...l, product_weight: weight } : l
        )
      }));
    } catch (error) {
      console.error('Failed to save weight:', error);
      toast.error('Failed to save weight: ' + (error.response?.data?.error || error.message));
    } finally {
      setSavingWeight(prev => ({ ...prev, [line.id]: false }));
    }
  };

  const loadOrderDetails = async () => {
    try {
      const response = await manufacturingAPI.customerOrders.get(orderId);
      setOrder(response.data);
    } catch (error) {
      console.error('Failed to load order details:', error);
      toast.error('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const loadWorkOrders = async () => {
    try {
      const response = await manufacturingAPI.workOrders.list();
      const allWorkOrders = response.data.results || response.data;
      const filtered = allWorkOrders.filter(wo => 
        wo.customer_order === parseInt(orderId)
      );
      setWorkOrders(filtered);
    } catch (error) {
      console.error('Failed to load work orders:', error);
    }
  };

  const handleCreateWorkOrders = async () => {
    try {
      const response = await manufacturingAPI.customerOrders.createWorkOrders(orderId);
      const data = response.data;
      const created = data.created || 0;
      const consolidated = data.consolidated || 0;
      
      let message = '';
      if (created > 0 && consolidated > 0) {
        message = `Created ${created} new work orders and updated ${consolidated} existing work orders`;
      } else if (created > 0) {
        message = `Created ${created} new work orders`;
      } else if (consolidated > 0) {
        message = `Updated ${consolidated} existing work orders`;
      } else {
        message = data.message || 'Work orders processed';
      }
      
      toast.success(message, { autoClose: 5000 });
      loadOrderDetails();
      loadWorkOrders();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create work orders');
    }
  };

  const [sendingToV2, setSendingToV2] = useState(false);
  const [unsendingV2, setUnsendingV2] = useState(false);
  // v2 feature flags — null until loaded. Fail open: if the fetch errors we
  // leave this null so the v1 "Create Work Orders" button stays visible.
  const [v2Flags, setV2Flags] = useState(null);

  useEffect(() => {
    let cancelled = false;
    v2.featureFlags.get()
      .then((res) => { if (!cancelled) setV2Flags(res.data || {}); })
      .catch(() => { /* fail open — leave flags null, v1 stays visible */ });
    return () => { cancelled = true; };
  }, []);

  const handleSendToV2 = async () => {
    setSendingToV2(true);
    try {
      const response = await v2.explode.customerOrder(orderId);
      const trees = response.data?.tree_ids?.length ?? 0;
      // Lines the backend skipped. `kind` is newer data — a skipped line
      // without one is a genuine failure (the only case before kind existed).
      const skippedLines = response.data?.skipped_lines || [];
      const coveredLines = skippedLines.filter((s) => s?.kind === 'covered');
      const failedLines = skippedLines.filter((s) => (s?.kind || 'failed') === 'failed');
      const coveredNote = coveredLines.length
        ? ` — ${coveredLines.length} line${coveredLines.length === 1 ? '' : 's'} covered from stock`
        : '';

      if (trees > 0) {
        toast.success(
          `Sent to V2 shop floor — ${trees} work order tree${trees === 1 ? '' : 's'} created${coveredNote}`,
          { autoClose: 5000 }
        );
      } else if (coveredLines.length > 0 && failedLines.length === 0) {
        // Nothing to manufacture is a GOOD outcome, not a warning.
        toast.info(
          `Nothing to produce — ${coveredLines.length} line${coveredLines.length === 1 ? '' : 's'} `
          + 'fully covered by allocated stock. No work orders needed.',
          { autoClose: 6000 }
        );
      } else {
        toast.info(`No new V2 trees created — all lines were already exploded or had nothing to manufacture${coveredNote}`);
      }

      if (failedLines.length > 0) {
        const first = failedLines[0];
        toast.warning(
          `${failedLines.length} line${failedLines.length === 1 ? '' : 's'} could not be sent — `
          + `${first.product_code ? `${first.product_code}: ` : ''}${first.reason || 'unknown reason'}`
          + (failedLines.length > 1 ? ` (+${failedLines.length - 1} more)` : ''),
          { autoClose: 8000 }
        );
      }
    } catch (error) {
      // A 409 now means production is closed (complete/cancelled) — surface the
      // backend's message verbatim rather than assuming "already sent".
      if (error.response?.status === 409) {
        toast.error(error.response?.data?.error || 'This order can no longer be sent to V2');
      } else {
        toast.error(error.response?.data?.error || 'Failed to send order to V2');
      }
    } finally {
      setSendingToV2(false);
    }
  };

  const handleUndoV2 = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      'Cancel every V2 work order for this customer order?\n\n'
      + 'This rolls back the V2 explosion so you can re-test. '
      + 'V1 work orders are NOT affected. Already-completed V2 work orders '
      + 'are kept (their ledger receipts are append-only).'
    )) return;
    setUnsendingV2(true);
    try {
      const response = await v2.explode.unsendCustomerOrder(orderId);
      const data = response.data || {};
      const cancelled = data.cancelled_count ?? 0;
      const completedSkipped = data.completed_skipped_count ?? 0;
      if (cancelled === 0 && completedSkipped === 0) {
        toast.info('Nothing to undo — no active V2 work orders for this order');
      } else {
        let msg = `Undone — cancelled ${cancelled} V2 work order${cancelled === 1 ? '' : 's'}`;
        if (completedSkipped > 0) {
          msg += `. ${completedSkipped} already-completed left untouched (re-cancel via admin if needed).`;
        }
        toast.success(msg, { autoClose: 6000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to undo V2 send');
    } finally {
      setUnsendingV2(false);
    }
  };

  const [revertingToV1, setRevertingToV1] = useState(false);

  // Admin-only swap-back: retire this order's active V2 work orders (completed
  // work and its allocations are KEPT) and re-plan the outstanding quantity as
  // fresh V1 work orders. Runs a server dry-run first and shows the numbers
  // before anything is committed.
  const handleRevertToV1 = async () => {
    setRevertingToV1(true);
    try {
      const preview = (await v2.admin.revertToV1({ order_ids: [Number(orderId)] })).data;
      const retired = preview.v2_wos_retired?.length ?? 0;
      const planned = preview.v1_creation?.work_orders?.length ?? 0;
      const wip = preview.wip_warnings?.length ?? 0;
      const skipReason = preview.orders_skipped?.[0]?.reason;

      if (retired === 0 && planned === 0) {
        toast.info(
          skipReason === 'already_has_active_v1_wos'
            ? 'Nothing to do — this order already has active V1 work orders covering it.'
            : 'Nothing to revert — no active V2 work orders and nothing outstanding to plan.'
        );
        return;
      }

      let msg =
        'Revert this order to V1?\n\n'
        + `• ${retired} V2 work order${retired === 1 ? '' : 's'} will be retired (completed production and allocations are kept)\n`
        + `• ${planned} V1 work order${planned === 1 ? '' : 's'} will be created/updated for the outstanding quantity`;
      if (wip > 0) {
        msg += `\n\n⚠ ${wip} V2 work order${wip === 1 ? ' has' : 's have'} material already issued. `
          + 'That material stays consumed — review stock levels afterwards and post a manual adjustment if it is reusable.';
      }
      // eslint-disable-next-line no-alert
      if (!window.confirm(msg)) return;

      const result = (await v2.admin.revertToV1({
        order_ids: [Number(orderId)], dry_run: false,
      })).data;
      const done = result.v1_creation || {};
      toast.success(
        `Reverted to V1 — retired ${result.v2_wos_retired?.length ?? 0} V2 work orders, `
        + `created ${done.created ?? 0} / updated ${done.consolidated ?? 0} V1 work orders`,
        { autoClose: 8000 }
      );
      if ((result.wip_warnings?.length ?? 0) > 0) {
        toast.warning(
          `${result.wip_warnings.length} retired work order(s) had issued material — check stock levels.`,
          { autoClose: false }
        );
      }
      loadOrderDetails();
      loadWorkOrders();
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error(error.response?.data?.error || 'Revert refused — V1 work-order creation is disabled.');
      } else {
        toast.error(error.response?.data?.error || 'Failed to revert order to V1');
      }
    } finally {
      setRevertingToV1(false);
    }
  };

  const handleShipQuantity = async () => {
    if (!selectedLine || !shipQuantity) return;

    try {
      await manufacturingAPI.customerOrders.shipQuantity(orderId, {
        line_id: selectedLine.id,
        quantity: parseInt(shipQuantity)
      });
      
      toast.success(`Shipped ${shipQuantity} units of ${selectedLine.product_display}`);
      setShipDialog(false);
      setSelectedLine(null);
      setShipQuantity('');
      loadOrderDetails();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to ship quantity');
    }
  };

  const openShipDialog = (line) => {
    setSelectedLine(line);
    
    const allocated = line.quantity_allocated || 0;
    const shipped = line.quantity_shipped || 0;
    const maxShippable = allocated - shipped;
    
    setShipQuantity(maxShippable.toString());
    setShipDialog(true);
  };

  // Ship All functionality
  const openShipAllDialog = () => {
    const shippable = order.lines?.filter(line => {
      const allocated = line.quantity_allocated || 0;
      const shipped = line.quantity_shipped || 0;
      return (allocated - shipped) > 0;
    }).map(line => ({
      id: line.id,
      product: line.product_display,
      quantity: (line.quantity_allocated || 0) - line.quantity_shipped
    })) || [];

    if (shippable.length === 0) {
      toast.info('No items available to ship');
      return;
    }

    setShippableLinesSummary(shippable);
    setShipAllDialog(true);
  };

  const confirmShipAll = async () => {
    setShipAllDialog(false);
    
    const shippableLines = order.lines.filter(line => {
      const allocated = line.quantity_allocated || 0;
      const shipped = line.quantity_shipped || 0;
      return (allocated - shipped) > 0;
    });

    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    for (const line of shippableLines) {
      const quantityToShip = (line.quantity_allocated || 0) - line.quantity_shipped;
      
      try {
        await manufacturingAPI.customerOrders.shipQuantity(orderId, {
          line_id: line.id,
          quantity: quantityToShip
        });
        successCount++;
        console.log(`Shipped ${quantityToShip} of ${line.product_display}`);
      } catch (error) {
        errorCount++;
        const errorMsg = error.response?.data?.error || 'Unknown error';
        errors.push(`${line.product_display}: ${errorMsg}`);
        console.error(`Failed to ship ${line.product_display}:`, errorMsg);
      }
    }

    if (successCount > 0) {
      toast.success(`Successfully shipped ${successCount} line item(s)`, { autoClose: 5000 });
    }
    
    if (errorCount > 0) {
      toast.error(`Failed to ship ${errorCount} line item(s). Check console for details.`, { 
        autoClose: 8000 
      });
    }

    loadOrderDetails();
  };

  // Edit Delivery Info functions
  const openEditDeliveryDialog = () => {
    setDeliveryForm({
      delivery_name: order.delivery_name || '',
      delivery_contact_name: order.delivery_contact_name || '',
      delivery_phone: order.delivery_phone || '',
      delivery_line_1: order.delivery_line_1 || '',
      delivery_line_2: order.delivery_line_2 || '',
      delivery_line_3: order.delivery_line_3 || '',
      delivery_city: order.delivery_city || '',
      delivery_county: order.delivery_county || '',
      delivery_postcode: order.delivery_postcode || '',
      dx_note_1: order.dx_note_1 || '',
      dx_note_2: order.dx_note_2 || '',
      dx_note_3: order.dx_note_3 || '',
    });
    setEditDeliveryDialog(true);
  };

  const handleDeliveryFormChange = (field) => (event) => {
    setDeliveryForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSaveDeliveryInfo = async () => {
    setSavingDelivery(true);
    try {
      await manufacturingAPI.customerOrders.update(orderId, deliveryForm);
      toast.success('Delivery information updated');
      setEditDeliveryDialog(false);
      loadOrderDetails();
    } catch (error) {
      console.error('Failed to update delivery info:', error);
      toast.error(error.response?.data?.error || 'Failed to update delivery information');
    } finally {
      setSavingDelivery(false);
    }
  };

  // Delete order functions
  const handlePreviewDelete = async () => {
    setLoadingPreview(true);
    try {
      const response = await manufacturingAPI.customerOrders.previewDelete(orderId);
      setDeletePreview(response.data);
      setDeletePreviewDialog(true);
    } catch (error) {
      console.error('Failed to preview delete:', error);
      toast.error(error.response?.data?.error || 'Failed to preview delete');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      const response = await manufacturingAPI.customerOrders.deleteWithCleanup(orderId);
      toast.success(response.data.message);
      
      // Show summary
      const results = response.data.results;
      if (results.deleted.length > 0) {
        toast.info(`Deleted ${results.deleted.length} work order(s)`, { autoClose: 5000 });
      }
      if (results.reduced.length > 0) {
        toast.info(`Reduced ${results.reduced.length} work order(s)`, { autoClose: 5000 });
      }
      
      setConfirmDeleteDialog(false);
      setDeletePreviewDialog(false);
      navigate('/manufacturing/customer-orders');
    } catch (error) {
      console.error('Failed to delete order:', error);
      toast.error(error.response?.data?.error || 'Failed to delete order');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(value || 0);
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'info',
      'confirmed': 'primary',
      'in_production': 'warning',
      'ready_to_ship': 'success',
      'partially_shipped': 'primary',
      'complete': 'success',
      'cancelled': 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'new': 'New',
      'confirmed': 'Confirmed',
      'in_production': 'In Production',
      'ready_to_ship': 'Ready to Ship',
      'partially_shipped': 'Partially Shipped',
      'complete': 'Complete',
      'cancelled': 'Cancelled'
    };
    return labels[status] || status;
  };

  const getDaysUntilDue = (dueDate) => {
    const days = Math.floor((new Date(dueDate) - new Date()) / (1000 * 60 * 60 * 24));
    return days;
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const response = await manufacturingAPI.customerOrders.updateStatus(orderId, newStatus);
      setOrder(response.data);
      toast.success(`Order status changed to ${getStatusLabel(newStatus)}`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update status');
    }
  };

  // Helper to check if delivery info exists
  const hasDeliveryInfo = () => {
    return order.delivery_name || order.delivery_line_1 || order.delivery_city || order.delivery_postcode || order.delivery_address;
  };

  // Helper to check if DX notes exist
  const hasDxNotes = () => {
    return order.dx_note_1 || order.dx_note_2 || order.dx_note_3;
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Loading Order Details...</Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (!order) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Order not found</Alert>
        <Button onClick={() => navigate('/manufacturing/customer-orders')} sx={{ mt: 2 }}>
          Back to Orders
        </Button>
      </Box>
    );
  }

  const daysUntilDue = getDaysUntilDue(order.due_date);
  const isOverdue = daysUntilDue < 0;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={() => navigate('/manufacturing/customer-orders')}
          sx={{ mb: 2 }}
        >
          Back to Orders
        </Button>
        
        <Box className="page-header-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart color="primary" />
              {order.order_number}
              {canChangeStatus ? (
                <FormControl size="small" sx={{ ml: 2, minWidth: 160 }}>
                  <Select
                    value={order.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    renderValue={(value) => (
                      <Chip label={getStatusLabel(value)} color={getStatusColor(value)} size="small" />
                    )}
                    sx={{ '.MuiSelect-select': { py: 0.5 } }}
                  >
                    {[
                      ['new', 'New'],
                      ['confirmed', 'Confirmed'],
                      ['in_production', 'In Production'],
                      ['ready_to_ship', 'Ready to Ship'],
                      ['partially_shipped', 'Partially Shipped'],
                      ['complete', 'Complete'],
                      ['cancelled', 'Cancelled'],
                    ].map(([value, label]) => (
                      <MenuItem key={value} value={value}>
                        <Chip label={label} color={getStatusColor(value)} size="small" sx={{ cursor: 'pointer' }} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Chip
                  label={getStatusLabel(order.status)}
                  color={getStatusColor(order.status)}
                  sx={{ ml: 2 }}
                />
              )}
              {order.status === 'complete' && (
                <CheckCircle color="success" sx={{ ml: 1 }} />
              )}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              Customer: <strong>{order.customer_name}</strong>
            </Typography>
            {order.customer_reference && (
              <Typography variant="body2" color="text.secondary">
                Reference: {order.customer_reference}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {shouldShowV1CreateWorkOrders(order.status, v2Flags) && (
              <Button
                variant="contained"
                startIcon={<Factory />}
                onClick={handleCreateWorkOrders}
              >
                Create Work Orders
              </Button>
            )}

            {shouldShowV2ProductionActions(order.status) && (
              <Tooltip title="Run per-line BOM explosion and create V2 work orders on the V2 shop floor terminal">
                <span>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<Science />}
                    onClick={handleSendToV2}
                    disabled={sendingToV2 || unsendingV2}
                    data-testid="create-work-orders-v2"
                  >
                    {sendingToV2 ? 'Creating…' : 'Create Work Orders (V2)'}
                  </Button>
                </span>
              </Tooltip>
            )}

            {shouldShowV2ProductionActions(order.status) && (
              <Tooltip title="Cancel every V2 work order for this customer order so you can re-test. V1 work orders are NOT affected.">
                <span>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<DeleteForever />}
                    onClick={handleUndoV2}
                    disabled={sendingToV2 || unsendingV2}
                  >
                    {unsendingV2 ? 'Undoing…' : 'Undo V2'}
                  </Button>
                </span>
              </Tooltip>
            )}

            {isAdmin && shouldShowV2ProductionActions(order.status) && (
              <Tooltip title="Swap this order back to V1: retire its active V2 work orders (completed production kept) and re-plan the outstanding quantity as V1 work orders. Shows a preview first.">
                <span>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Restore />}
                    onClick={handleRevertToV1}
                    disabled={sendingToV2 || unsendingV2 || revertingToV1}
                    data-testid="revert-to-v1"
                  >
                    {revertingToV1 ? 'Reverting…' : 'Revert to V1'}
                  </Button>
                </span>
              </Tooltip>
            )}

            {isOrderProductionClosed(order.status) && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ alignSelf: 'center', fontStyle: 'italic' }}
                data-testid="v2-production-closed"
              >
                Order {getStatusLabel(order.status).toLowerCase()} — production closed.
              </Typography>
            )}

            <Badge badgeContent={dxConsignments.length} color="secondary">
              <Button
                variant="contained"
                color={order.status === 'complete' ? 'info' : 'primary'}
                startIcon={<LocalShipping />}
                onClick={() => navigate(`/manufacturing/customer-orders/${orderId}/dx-shipping`)}
              >
                {order.status === 'complete' || order.status === 'cancelled' ? 'View DX Shipments' : 'DX Shipping'}
              </Button>
            </Badge>

            <IconButton color="primary">
              <Edit />
            </IconButton>

            <Tooltip title="Delete Order">
              <IconButton 
                color="error" 
                onClick={handlePreviewDelete}
                disabled={loadingPreview}
              >
                {loadingPreview ? <CircularProgress size={24} /> : <Delete />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Alert Banners */}
      {isOverdue && order.status !== 'complete' && order.status !== 'cancelled' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>This order is {Math.abs(daysUntilDue)} days overdue!</strong>
        </Alert>
      )}

      {order.status === 'complete' && (
        <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>
          <strong>Order Complete!</strong> All items shipped on {formatDate(order.updated_at)}.
        </Alert>
      )}

      {order.status === 'cancelled' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>This order was cancelled.</strong>
        </Alert>
      )}

      {order.status === 'partially_shipped' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Partial shipment made.</strong> {order.total_quantity_shipped} of {order.total_quantity_ordered} items shipped.
        </Alert>
      )}

      {order.status === 'ready_to_ship' && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <strong>This order is ready to ship!</strong> All items are available.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left Column - Order Details */}
        <Grid item xs={12} md={8}>
          {/* Order Summary Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Order Summary</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CalendarToday fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Order Date</Typography>
                      <Typography variant="body2">{formatDate(order.order_date)}</Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Schedule fontSize="small" color={isOverdue ? 'error' : 'action'} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Due Date</Typography>
                      <Typography 
                        variant="body2" 
                        color={isOverdue ? 'error.main' : 'text.primary'}
                        fontWeight={isOverdue ? 'bold' : 'normal'}
                      >
                        {formatDate(order.due_date)}
                        {daysUntilDue === 0 && ' (Today)'}
                        {daysUntilDue > 0 && ` (${daysUntilDue} days)`}
                        {daysUntilDue < 0 && ` (${Math.abs(daysUntilDue)} days overdue)`}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {order.order_taken_by && (
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Person fontSize="small" color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Taken By</Typography>
                        <Typography variant="body2">{order.order_taken_by}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <AttachMoney fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Order Total</Typography>
                      <Typography variant="h6" color="primary">
                        {formatCurrency(order.total_order_value)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {/* Progress Bar */}
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Overall Progress
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {Math.round(order.completion_percentage || 0)}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={order.completion_percentage || 0}
                  sx={{ height: 10, borderRadius: 5 }}
                  color={order.completion_percentage >= 100 ? 'success' : 'primary'}
                />
              </Box>

              {/* Quantity Summary */}
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                    <Typography variant="h4">{order.total_quantity_ordered || 0}</Typography>
                    <Typography variant="caption">Ordered</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light' }}>
                    <Typography variant="h4">
                      {order.lines?.reduce((sum, line) => sum + (line.quantity_completed || 0), 0) || 0}
                    </Typography>
                    <Typography variant="caption">Completed</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
                    <Typography variant="h4">{order.total_quantity_shipped || 0}</Typography>
                    <Typography variant="caption">Shipped</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Order Line Items */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Order Line Items</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Line</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Ordered</TableCell>
                      <TableCell align="right">Allocated</TableCell>
                      <TableCell align="right">Produced</TableCell>
                      <TableCell align="right">Shipped</TableCell>
                      <TableCell align="center">Weight (kg)</TableCell>
                      <TableCell align="right">Line Total</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.lines && order.lines.map((line) => {
                      const lineTotal = line.quantity_ordered * (line.unit_price || 0);
                      const allocated = Math.floor(parseFloat(line.quantity_allocated || 0));
                      const completed = Math.floor(parseFloat(line.quantity_completed || 0));
                      const shipped = Math.floor(parseFloat(line.quantity_shipped || 0));
                      const ordered = Math.floor(parseFloat(line.quantity_ordered || 0));
                      
                      const availableToShip = Math.max(0, allocated - shipped);
                      const canShip = availableToShip > 0;

                      return (
                        <TableRow key={line.id}>
                          <TableCell>{line.line_number}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {line.product_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {line.product_display}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {line.unit_price ? formatCurrency(line.unit_price) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold">
                              {line.quantity_ordered}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={allocated}
                              size="small"
                              color={allocated >= ordered ? 'success' : allocated > 0 ? 'warning' : 'default'}
                              icon={<Inventory />}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography 
                              variant="body2"
                              color={completed >= ordered ? 'success.main' : 'text.primary'}
                            >
                              {completed}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography 
                              variant="body2"
                              color={shipped >= ordered ? 'success.main' : 'text.primary'}
                              fontWeight={shipped >= ordered ? 'bold' : 'normal'}
                            >
                              {shipped}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {(() => {
                              const savedWeight = parseFloat(line.product_weight) || 0;
                              const currentWeight = lineWeights[line.id] !== undefined ? lineWeights[line.id] : savedWeight;
                              const weightChanged = currentWeight !== savedWeight && currentWeight > 0;
                              const isSavingWeight = savingWeight[line.id];
                              return (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={currentWeight || ''}
                                    onChange={(e) => handleWeightChange(line.id, e.target.value)}
                                    placeholder="0"
                                    inputProps={{ min: 0, step: 0.1, style: { textAlign: 'center' } }}
                                    InputProps={{
                                      endAdornment: <InputAdornment position="end">kg</InputAdornment>,
                                    }}
                                    sx={{
                                      width: 120,
                                      '& .MuiOutlinedInput-root': {
                                        bgcolor: savedWeight > 0 ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 152, 0, 0.1)'
                                      }
                                    }}
                                  />
                                  {weightChanged && (
                                    <Tooltip title="Save weight to product">
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => handleSaveWeight(line)}
                                        disabled={isSavingWeight}
                                      >
                                        {isSavingWeight ? <CircularProgress size={16} /> : <Save />}
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {savedWeight > 0 && !weightChanged && (
                                    <Tooltip title="Weight saved">
                                      <Scale fontSize="small" color="success" />
                                    </Tooltip>
                                  )}
                                </Box>
                              );
                            })()}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold">
                              {formatCurrency(lineTotal)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              if (shipped >= ordered) {
                                return <Chip label="Complete" size="small" color="success" icon={<CheckCircle />} />;
                              }
                              
                              if (availableToShip > 0) {
                                if (allocated >= ordered) {
                                  return <Chip label="Ready to Ship" size="small" color="warning" icon={<LocalShipping />} />;
                                } else {
                                  return <Chip label="Partially Available" size="small" color="info" icon={<Inventory />} />;
                                }
                              }
                              
                              if (completed > 0) {
                                return <Chip label="In Progress" size="small" color="info" icon={<Build />} />;
                              }
                              
                              if (allocated > 0) {
                                return <Chip label="Partially Allocated" size="small" color="info" icon={<Inventory />} />;
                              }
                              
                              return <Chip label="Pending" size="small" />;
                            })()}
                          </TableCell>
                          <TableCell>
                            {canShip && (
                              <Tooltip title={`Ship ${availableToShip} available units`}>
                                <IconButton 
                                  size="small" 
                                  color="success"
                                  onClick={() => openShipDialog(line)}
                                >
                                  <LocalShipping />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    
                    {/* Total Row */}
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell colSpan={7} align="right">
                        <Typography variant="body1" fontWeight="bold">
                          Order Total:
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="h6" color="primary" fontWeight="bold">
                          {formatCurrency(order.total_order_value)}
                        </Typography>
                      </TableCell>
                      <TableCell colSpan={2}></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Ship All Button */}
              {(() => {
                const hasShippableItems = order.lines?.some(line => {
                  const allocated = line.quantity_allocated || 0;
                  const shipped = line.quantity_shipped || 0;
                  return (allocated - shipped) > 0;
                });

                if (!hasShippableItems || order.status === 'complete' || order.status === 'cancelled') {
                  return null;
                }

                return (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<LocalShipping />}
                      onClick={openShipAllDialog}
                      size="large"
                    >
                      Ship All Available Items
                    </Button>
                  </Box>
                );
              })()}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Additional Info */}
        <Grid item xs={12} md={4}>
          {/* Customer Information */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Customer Information</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold">{order.customer_name}</Typography>
                {order.sage_customer_code && (
                  <Typography variant="caption" color="text.secondary">
                    Sage Code: {order.sage_customer_code}
                  </Typography>
                )}
              </Box>

              {order.booking_contacts && order.booking_contacts.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Phone fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">Contact</Typography>
                  </Box>
                  {order.booking_contacts.map((contact, idx) => (
                    <Typography key={idx} variant="body2">{contact}</Typography>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Delivery Information Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">Delivery Information</Typography>
                <Tooltip title="Edit Delivery Info">
                  <IconButton size="small" color="primary" onClick={openEditDeliveryDialog}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {hasDeliveryInfo() ? (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <LocationOn fontSize="small" color="action" sx={{ mt: 0.5 }} />
                    <Box>
                      {order.delivery_name && (
                        <Typography variant="body2" fontWeight="bold">
                          {order.delivery_name}
                        </Typography>
                      )}
                      {order.delivery_line_1 && (
                        <Typography variant="body2">{order.delivery_line_1}</Typography>
                      )}
                      {order.delivery_line_2 && (
                        <Typography variant="body2">{order.delivery_line_2}</Typography>
                      )}
                      {order.delivery_line_3 && (
                        <Typography variant="body2">{order.delivery_line_3}</Typography>
                      )}
                      {order.delivery_city && (
                        <Typography variant="body2">{order.delivery_city}</Typography>
                      )}
                      {order.delivery_county && (
                        <Typography variant="body2">{order.delivery_county}</Typography>
                      )}
                      {order.delivery_postcode && (
                        <Typography variant="body2" fontWeight="bold" sx={{ mt: 0.5 }}>
                          {order.delivery_postcode}
                        </Typography>
                      )}

                      {/* Fallback to legacy delivery_address if no structured fields */}
                      {!order.delivery_name && !order.delivery_line_1 && !order.delivery_city && order.delivery_address && (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                          {order.delivery_address}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Delivery Contact */}
                  {(order.delivery_contact_name || order.delivery_phone) && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        Delivery Contact
                      </Typography>
                      {order.delivery_contact_name && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Person fontSize="small" color="action" />
                          <Typography variant="body2" fontWeight="bold">
                            {order.delivery_contact_name}
                          </Typography>
                        </Box>
                      )}
                      {order.delivery_phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Phone fontSize="small" color="action" />
                          <Typography variant="body2">
                            {order.delivery_phone}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <LocationOn color="disabled" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No delivery information
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Edit />}
                    onClick={openEditDeliveryDialog}
                    sx={{ mt: 1 }}
                  >
                    Add Delivery Info
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* DX Delivery Notes Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">DX Delivery Notes</Typography>
                <Tooltip title="Edit DX Notes">
                  <IconButton size="small" color="primary" onClick={openEditDeliveryDialog}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {hasDxNotes() ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {order.dx_note_1 && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Chip label="1" size="small" color="primary" sx={{ minWidth: 24, height: 24 }} />
                      <Typography variant="body2">{order.dx_note_1}</Typography>
                    </Box>
                  )}
                  {order.dx_note_2 && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Chip label="2" size="small" color="primary" sx={{ minWidth: 24, height: 24 }} />
                      <Typography variant="body2">{order.dx_note_2}</Typography>
                    </Box>
                  )}
                  {order.dx_note_3 && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Chip label="3" size="small" color="primary" sx={{ minWidth: 24, height: 24 }} />
                      <Typography variant="body2">{order.dx_note_3}</Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Notes color="disabled" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No DX delivery notes
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Edit />}
                    onClick={openEditDeliveryDialog}
                    sx={{ mt: 1 }}
                  >
                    Add DX Notes
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Production Status */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Production Status</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                    <Typography variant="h5" color="primary">
                      {order.production_status?.total_work_orders || 0}
                    </Typography>
                    <Typography variant="caption">Total WOs</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'success.light' }}>
                    <Typography variant="h5">
                      {order.production_status?.completed || 0}
                    </Typography>
                    <Typography variant="caption">Completed</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'warning.light' }}>
                    <Typography variant="h5">
                      {order.production_status?.in_progress || 0}
                    </Typography>
                    <Typography variant="caption">In Progress</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'grey.200' }}>
                    <Typography variant="h5">
                      {order.production_status?.pending || 0}
                    </Typography>
                    <Typography variant="caption">Pending</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Related Work Orders */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Related Work Orders</Typography>
              <Divider sx={{ mb: 2 }} />
              
              {workOrders.length === 0 ? (
                <Alert severity="info">
                  No work orders created yet.
                </Alert>
              ) : (
                <List dense>
                  {workOrders.map((wo) => (
                    <ListItem 
                      key={wo.id}
                      sx={{ 
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {wo.work_order_number}
                            </Typography>
                            <Chip 
                              label={wo.status} 
                              size="small" 
                              color={
                                wo.status === 'completed' ? 'success' : 
                                wo.status === 'in_progress' ? 'warning' : 
                                wo.status === 'released' ? 'info' :
                                'default'
                              }
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" display="block">
                              {wo.output_item_name} - {wo.output_item_display}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {wo.quantity_completed}/{wo.quantity_to_produce} | 
                              Progress: {Math.round(wo.progress_percentage || 0)}%
                            </Typography>
                          </Box>
                        }
                      />
                      {wo.status === 'completed' && <CheckCircle color="success" />}
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          {/* V2 Production (additive; never breaks the V1 page above) */}
          <OrderV2ProductionSection
            orderId={orderId}
            variant="full"
            orderStatus={order.status}
            onChanged={() => { loadOrderDetails(); loadWorkOrders(); }}
          />

          {/* Order lifecycle timeline (lazy-loaded on first expand) */}
          <OrderTimelineV2 orderId={orderId} />

          {/* Notes */}
          {order.notes && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Notes</Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {order.notes}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Ship Quantity Dialog */}
      <Dialog open={shipDialog} onClose={() => setShipDialog(false)}>
        <DialogTitle>Ship Quantity</DialogTitle>
        <DialogContent>
          {selectedLine && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Product:</strong> {selectedLine.product_name}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Available to ship:</strong> {' '}
                {Math.max(0, (selectedLine.quantity_allocated || 0) - selectedLine.quantity_shipped)} units
              </Typography>
              
              <TextField
                fullWidth
                label="Quantity to Ship"
                type="number"
                value={shipQuantity}
                onChange={(e) => setShipQuantity(e.target.value)}
                inputProps={{ 
                  min: 1, 
                  max: Math.max(0, (selectedLine.quantity_allocated || 0) - selectedLine.quantity_shipped)
                }}
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShipDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleShipQuantity}
            disabled={!shipQuantity || parseInt(shipQuantity) <= 0}
          >
            Confirm Shipment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ship All Dialog */}
      <Dialog open={shipAllDialog} onClose={() => setShipAllDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ship All Available Items</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
            The following items will be shipped:
          </Typography>
          <List dense>
            {shippableLinesSummary.map((item, idx) => (
              <ListItem key={idx} sx={{ py: 1 }}>
                <ListItemText
                  primary={<Typography variant="body2" fontWeight="bold">{item.product}</Typography>}
                  secondary={`${item.quantity} units`}
                />
              </ListItem>
            ))}
          </List>
          <Alert severity="info" sx={{ mt: 2 }}>
            This will create shipment records and reduce inventory for all items shown above.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShipAllDialog(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={confirmShipAll} startIcon={<LocalShipping />}>
            Confirm Ship All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Delivery Info Dialog */}
      <Dialog 
        open={editDeliveryDialog} 
        onClose={() => setEditDeliveryDialog(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShipping color="primary" />
            Edit Delivery Information
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Address Section */}
            <Typography variant="subtitle2" color="text.secondary">
              Delivery Address
            </Typography>
            <TextField
              fullWidth
              label="Company / School Name"
              value={deliveryForm.delivery_name}
              onChange={handleDeliveryFormChange('delivery_name')}
              placeholder="e.g. Heritage High School"
            />
            <TextField
              fullWidth
              label="Address Line 1"
              value={deliveryForm.delivery_line_1}
              onChange={handleDeliveryFormChange('delivery_line_1')}
              placeholder="e.g. The Two Counties Trust"
            />
            <TextField
              fullWidth
              label="Address Line 2"
              value={deliveryForm.delivery_line_2}
              onChange={handleDeliveryFormChange('delivery_line_2')}
              placeholder="e.g. Boughton Lane, Clowne"
            />
            <TextField
              fullWidth
              label="Address Line 3"
              value={deliveryForm.delivery_line_3}
              onChange={handleDeliveryFormChange('delivery_line_3')}
              placeholder="Optional"
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={deliveryForm.delivery_city}
                  onChange={handleDeliveryFormChange('delivery_city')}
                  placeholder="e.g. Chesterfield"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="County"
                  value={deliveryForm.delivery_county}
                  onChange={handleDeliveryFormChange('delivery_county')}
                  placeholder="e.g. Derbyshire"
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              label="Postcode"
              value={deliveryForm.delivery_postcode}
              onChange={handleDeliveryFormChange('delivery_postcode')}
              placeholder="e.g. S43 4QG"
              inputProps={{ style: { textTransform: 'uppercase' } }}
            />

            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="text.secondary">
              Delivery Contact
            </Typography>
            <TextField
              fullWidth
              label="Contact Name"
              value={deliveryForm.delivery_contact_name}
              onChange={handleDeliveryFormChange('delivery_contact_name')}
              placeholder="e.g. John Smith"
            />
            <TextField
              fullWidth
              label="Phone Number"
              value={deliveryForm.delivery_phone}
              onChange={handleDeliveryFormChange('delivery_phone')}
              placeholder="e.g. 0113 123456"
            />

            {/* DX Notes Section */}
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="text.secondary">
              DX Delivery Notes
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              These notes will be included in DX shipping consignments
            </Typography>
            <TextField
              fullWidth
              label="DX Note 1"
              value={deliveryForm.dx_note_1}
              onChange={handleDeliveryFormChange('dx_note_1')}
              placeholder="e.g. ADD NOTE ON DX"
              helperText="First delivery instruction"
            />
            <TextField
              fullWidth
              label="DX Note 2"
              value={deliveryForm.dx_note_2}
              onChange={handleDeliveryFormChange('dx_note_2')}
              placeholder="e.g. ACCESS VIA LOW BRIDGE"
              helperText="Second delivery instruction"
            />
            <TextField
              fullWidth
              label="DX Note 3"
              value={deliveryForm.dx_note_3}
              onChange={handleDeliveryFormChange('dx_note_3')}
              placeholder="e.g. HEIGHT 2.74M"
              helperText="Third delivery instruction"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setEditDeliveryDialog(false)} 
            startIcon={<Close />}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveDeliveryInfo}
            disabled={savingDelivery}
            startIcon={savingDelivery ? null : <Save />}
          >
            {savingDelivery ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Preview Dialog */}
      <Dialog 
        open={deletePreviewDialog} 
        onClose={() => setDeletePreviewDialog(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteForever />
            Delete Order: {deletePreview?.order_number}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {deletePreview && (
            <Box>
              {/* Order Summary */}
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  You are about to delete order <strong>{deletePreview.order_number}</strong> for{' '}
                  <strong>{deletePreview.customer_name}</strong> with{' '}
                  <strong>{deletePreview.total_lines}</strong> line items totaling{' '}
                  <strong>{deletePreview.total_quantity_ordered}</strong> units.
                </Typography>
              </Alert>

              {/* Impact Summary */}
              <Typography variant="h6" gutterBottom>Impact Summary</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light', color: 'error.contrastText' }}>
                    <Typography variant="h4">{deletePreview.summary.v2_work_orders_to_delete ?? 0}</Typography>
                    <Typography variant="caption">V2 Work Orders to Delete</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light', color: 'error.contrastText' }}>
                    <Typography variant="h4">{deletePreview.summary.work_orders_to_delete}</Typography>
                    <Typography variant="caption">Work Orders to Delete</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light' }}>
                    <Typography variant="h4">{deletePreview.summary.work_orders_to_reduce}</Typography>
                    <Typography variant="caption">Work Orders to Reduce</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light' }}>
                    <Typography variant="h4">{deletePreview.summary.work_orders_to_unlink}</Typography>
                    <Typography variant="caption">Work Orders to Unlink</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* V2 Work Orders - deleted with the order (whole trees) */}
              {(deletePreview.v2_work_orders?.length || 0) > 0 && (
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography color="error" fontWeight="bold">
                      V2 Work Orders to DELETE ({deletePreview.v2_work_orders.length}
                      {deletePreview.summary.v2_trees_to_delete
                        ? ` across ${deletePreview.summary.v2_trees_to_delete} tree${deletePreview.summary.v2_trees_to_delete === 1 ? '' : 's'}`
                        : ''})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Deleting this order permanently removes these V2 work orders (the full
                      tree for each order line) and releases their material reservations.
                      {(deletePreview.summary.v2_work_orders_stock_kept ?? 0) > 0 && (
                        <> Work has already been done in {deletePreview.summary.v2_work_orders_stock_kept === deletePreview.v2_work_orders.length ? 'this tree' : 'some trees'} -
                        completed work is <strong>not reversed</strong>: sub-assemblies and output
                        already made stay in stock (and become free stock), and consumed raw
                        material stays consumed.</>
                      )}
                    </Alert>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>WO Number</strong></TableCell>
                            <TableCell><strong>Item</strong></TableCell>
                            <TableCell align="right"><strong>Qty</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell><strong>Level</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {deletePreview.v2_work_orders.map((wo) => (
                            <TableRow key={wo.id} sx={wo.has_progress ? { bgcolor: 'warning.lighter' } : undefined}>
                              <TableCell>{wo.wo_number}</TableCell>
                              <TableCell>{wo.item_code} - {wo.item_name}</TableCell>
                              <TableCell align="right">{wo.quantity_to_produce}</TableCell>
                              <TableCell>
                                {wo.status}{wo.has_progress ? ' (work done - output kept in stock)' : ''}
                              </TableCell>
                              <TableCell>{wo.is_top_level ? 'Top level' : 'Sub-assembly'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Work Orders to Delete */}
              {deletePreview.work_orders_to_delete.length > 0 && (
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography color="error" fontWeight="bold">
                      Work Orders to DELETE ({deletePreview.work_orders_to_delete.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Work Order</TableCell>
                            <TableCell>Item</TableCell>
                            <TableCell align="right">Quantity</TableCell>
                            <TableCell>Step</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Reason</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {deletePreview.work_orders_to_delete.map((wo, idx) => (
                            <TableRow key={idx}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold" color="error">
                                  {wo.work_order_number}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{wo.item_code}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {wo.item_name}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">{wo.current_quantity}</TableCell>
                              <TableCell>
                                {wo.total_steps > 1 ? `${wo.step_number}/${wo.total_steps}` : '-'}
                              </TableCell>
                              <TableCell>
                                <Chip label={wo.status} size="small" />
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption">{wo.reason}</Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Work Orders to Reduce */}
              {deletePreview.work_orders_to_reduce.length > 0 && (
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography color="warning.dark" fontWeight="bold">
                      Work Orders to REDUCE ({deletePreview.work_orders_to_reduce.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Work Order</TableCell>
                            <TableCell>Item</TableCell>
                            <TableCell align="right">Current Qty</TableCell>
                            <TableCell align="right">New Qty</TableCell>
                            <TableCell align="right">Reduce By</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {deletePreview.work_orders_to_reduce.map((wo, idx) => (
                            <TableRow key={idx}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold">
                                  {wo.work_order_number}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{wo.item_code}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {wo.item_name}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">{wo.current_quantity}</TableCell>
                              <TableCell align="right">
                                <Typography color="success.main" fontWeight="bold">
                                  {wo.new_quantity}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography color="error">-{wo.reduce_by}</Typography>
                              </TableCell>
                              <TableCell>
                                <Chip label={wo.status} size="small" />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Work Orders to Unlink Only */}
              {deletePreview.work_orders_to_unlink.length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography color="info.dark" fontWeight="bold">
                      Work Orders to UNLINK only ({deletePreview.work_orders_to_unlink.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Work Order</TableCell>
                            <TableCell>Item</TableCell>
                            <TableCell align="right">Quantity</TableCell>
                            <TableCell align="right">Completed</TableCell>
                            <TableCell>Reason</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {deletePreview.work_orders_to_unlink.map((wo, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{wo.work_order_number}</TableCell>
                              <TableCell>
                                <Typography variant="body2">{wo.item_code}</Typography>
                              </TableCell>
                              <TableCell align="right">{wo.current_quantity}</TableCell>
                              <TableCell align="right">{wo.quantity_completed}</TableCell>
                              <TableCell>
                                <Typography variant="caption">{wo.reason}</Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Stock Allocations to Release */}
              {deletePreview.allocations_to_release.length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography fontWeight="bold">
                      Stock Allocations to Release ({deletePreview.allocations_to_release.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Line</TableCell>
                            <TableCell>Item</TableCell>
                            <TableCell align="right">Allocated Qty</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {deletePreview.allocations_to_release.map((alloc, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{alloc.line_number}</TableCell>
                              <TableCell>{alloc.item_code}</TableCell>
                              <TableCell align="right">{alloc.quantity_allocated}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Component Reservations to Release */}
              {deletePreview.reservations_to_release.length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography fontWeight="bold">
                      Component Reservations to Release ({deletePreview.reservations_to_release.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Work Order</TableCell>
                            <TableCell>Component</TableCell>
                            <TableCell align="right">Reserved</TableCell>
                            <TableCell align="right">To Release</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {deletePreview.reservations_to_release.map((res, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{res.work_order_number}</TableCell>
                              <TableCell>{res.item_code}</TableCell>
                              <TableCell align="right">{res.quantity_reserved}</TableCell>
                              <TableCell align="right">
                                <Typography color="success.main">{res.to_release}</Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* No impact message */}
              {deletePreview.summary.work_orders_to_delete === 0 &&
               deletePreview.summary.work_orders_to_reduce === 0 &&
               deletePreview.summary.work_orders_to_unlink === 0 &&
               (deletePreview.summary.v2_work_orders_to_delete ?? 0) === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No work orders are linked to this order. Only stock allocations will be released.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePreviewDialog(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={() => setConfirmDeleteDialog(true)}
            startIcon={<DeleteForever />}
          >
            Proceed with Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Final Confirmation Dialog */}
      <Dialog open={confirmDeleteDialog} onClose={() => setConfirmDeleteDialog(false)}>
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'error.contrastText' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning />
            Final Confirmation
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              This action cannot be undone!
            </Typography>
          </Alert>
          <Typography variant="body1">
            Are you absolutely sure you want to delete order{' '}
            <strong>{deletePreview?.order_number}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary={`Delete ${deletePreview?.summary?.work_orders_to_delete || 0} work order(s)`} />
            </ListItem>
            <ListItem>
              <ListItemText primary={`Reduce ${deletePreview?.summary?.work_orders_to_reduce || 0} work order(s)`} />
            </ListItem>
            <ListItem>
              <ListItemText primary={`Release ${deletePreview?.summary?.allocations_to_release || 0} stock allocation(s)`} />
            </ListItem>
            <ListItem>
              <ListItemText primary="Permanently delete the order and all line items" />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteDialog(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteForever />}
          >
            {deleting ? 'Deleting...' : 'Yes, Delete Order'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}